import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { NavLink, useLocation, useParams } from 'react-router-dom';

import FountainLogo from '@/assets/icons/fountain-logo.svg';
import SourceLogo from '@/assets/icons/source-logo.svg';
import WxNavbarHost from '@/components/pages/wx-navbar-host';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenuButton,
} from '@/components/ui/sidebar';
import { Typography } from '@/components/ui/typography';
import { useAuthContext } from '@/contexts/auth-context';
import { useNavigationPaths } from '@/hooks/use-navigation-paths';

export function AppSidebar() {
  const { pathname } = useLocation();
  const { commitHash } = useParams();

  // Staging deploys are served under a `/<commitHash>` prefix that the wx-navbar
  // config (sourced from monolith) is unaware of. Strip it so the active-state
  // resolver sees a path it can match against `/<accountSlug>/...`.
  const hostPathname =
    commitHash && pathname.startsWith(`/${commitHash}`)
      ? pathname.slice(`/${commitHash}`.length) || '/'
      : pathname;
  const {
    dashboardPath,
    allOpeningsPath,
    allCampaignsPath,
    sourceSettingsPath,
  } = useNavigationPaths();
  const {
    account,
    brandSquareLogoUrl,
    brandLogoUrl,
    whoami,
    isAdmin,
    hasCampaignsPageAccess,
  } = useAuthContext();
  const { name } = account ?? {};

  // Memoize navbarConfig to prevent unnecessary re-renders
  // (same pattern as WxCopilotHost)
  const navbarConfig = useMemo(
    () => ({
      locale: whoami?.wx_navbar_config?.locale,
      mainNav: whoami?.wx_navbar_config?.mainConfig ?? [],
      secondaryNav: whoami?.wx_navbar_config?.secondaryConfig ?? [],
      dropdownNav: whoami?.wx_navbar_config?.dropdownConfig ?? [],
      employerDomain: whoami?.wx_navbar_config?.employerDomain,
      employerBasePath: whoami?.wx_navbar_config?.employerBasePath,
      inboxCount: whoami?.wx_navbar_config?.inboxCount,
      disableNavigation: whoami?.wx_navbar_config?.disableNavigation ?? false,
    }),
    [whoami?.wx_navbar_config],
  );

  const showWxNavbar =
    Boolean(whoami?.account_unified_auth) &&
    !whoami?.feature_flags?.['unified-navigation-exclusion'];

  const getTypographyVariant = (isActive: boolean) =>
    isActive ? 'body1' : 'subtitle1';

  if (showWxNavbar) {
    return (
      <nav>
        <WxNavbarHost
          userId={whoami?.wx_external_id}
          locale={navbarConfig.locale}
          companyId={whoami?.account_wx_external_id ?? ''}
          inboxCount={navbarConfig.inboxCount}
          employerDomain={navbarConfig.employerDomain}
          employerBasePath={navbarConfig.employerBasePath}
          disableNavigation={navbarConfig.disableNavigation}
          mainConfig={navbarConfig.mainNav}
          secondaryConfig={navbarConfig.secondaryNav}
          dropdownConfig={navbarConfig.dropdownNav}
          hostPathname={hostPathname}
        />
      </nav>
    );
  }

  return (
    <Sidebar>
      <SidebarHeader className="border-fx-neutral-200 flex h-[56px] items-start justify-center border-b px-4 [clip-path:inset(0_16px_0_16px)]">
        <img src={FountainLogo} alt="Fountain" className="h-[20px] w-[125px]" />
      </SidebarHeader>

      <SidebarContent className="px-2 py-4">
        <div className="flex items-center gap-2 p-1">
          <img src={SourceLogo} alt="Source" />
          <Typography variant="body2">Source</Typography>
        </div>

        <div className="py-2">
          <NavLink to={dashboardPath}>
            {({ isActive }) => (
              <SidebarMenuButton isActive={isActive}>
                <span className="w-8" />
                <Typography variant={getTypographyVariant(isActive)}>
                  <FormattedMessage
                    defaultMessage="Dashboard"
                    description="Left nav: org sourcing home and metrics"
                  />
                </Typography>
              </SidebarMenuButton>
            )}
          </NavLink>

          <NavLink to={allOpeningsPath}>
            {({ isActive }) => (
              <SidebarMenuButton isActive={isActive}>
                <span className="w-8" />
                <Typography variant={getTypographyVariant(isActive)}>
                  <FormattedMessage
                    defaultMessage="All Openings"
                    description="Left nav: list of jobs to add to dashboard"
                  />
                </Typography>
              </SidebarMenuButton>
            )}
          </NavLink>

          {hasCampaignsPageAccess && (
            <NavLink to={allCampaignsPath}>
              {({ isActive }) => (
                <SidebarMenuButton isActive={isActive}>
                  <span className="w-8" />
                  <Typography variant={getTypographyVariant(isActive)}>
                    <FormattedMessage
                      defaultMessage="Campaigns"
                      description="Left nav: list of all sourcing campaigns across openings"
                    />
                  </Typography>
                </SidebarMenuButton>
              )}
            </NavLink>
          )}

          {isAdmin && (
            <NavLink to={sourceSettingsPath}>
              {({ isActive }) => (
                <SidebarMenuButton isActive={isActive}>
                  <span className="w-8" />
                  <Typography variant={getTypographyVariant(isActive)}>
                    <FormattedMessage
                      defaultMessage="Settings"
                      description="Left nav: admin-only Source product settings"
                    />
                  </Typography>
                </SidebarMenuButton>
              )}
            </NavLink>
          )}
        </div>
      </SidebarContent>

      <SidebarFooter className="flex flex-row items-center gap-2">
        {(brandSquareLogoUrl || brandLogoUrl) && (
          <img
            src={brandSquareLogoUrl ?? brandLogoUrl ?? undefined}
            alt="Brand logo"
            className={brandSquareLogoUrl ? 'h-8 w-8' : 'h-8 w-16'}
          />
        )}
        <Typography variant="body1">{name}</Typography>
      </SidebarFooter>
    </Sidebar>
  );
}
